/* eslint-disable react-hooks/exhaustive-deps */
import { tokens } from "../../../theme";
import { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import PatchLabelDialog from "../PatchLabelDialog";
import {
  useCreateReleaseLabel,
  useListAllReleasesWithDetails,
  useUpdateReleaseStatus,
} from "../../../api/releases";
import Notification, {
  useNotification,
} from "../../../components/Notification";
import { releaseStyles } from "../styles";
import { nextPatchLabel, summaryStats } from "../summaryHelpers";
import ReleaseRow from "./ReleaseRow";
import ArchivedReleases from "./ArchivedReleases";
import ReleaseLabelsDrawer from "./ReleaseLabelsDrawer";
import { useConfirmationDialog } from "../../../components/ConfirmationDialog";

const patchLabelDialogDefault = {
  isOpen: false,
  release: "",
  releaseName: "",
  nextVersion: "",
  notReadOnly: false,
};

const ReleaseSummaryView = ({ selectedAppGroup, onStatsChange }) => {
  const theme = useTheme();
  const { summaryTable } = releaseStyles(tokens(theme.paletteId));

  const [releases, setReleases] = useState([]);
  // Name of the release whose labels drawer is open; the card already holds
  // every active label, so the drawer only has to be handed the release.
  const [selectedReleaseName, setSelectedReleaseName] = useState(null);

  const [patchLabelDialog, setPatchLabelDialog] = useState(
    patchLabelDialogDefault
  );
  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("");

  const { notificationComponent, displayNotification } = useNotification();

  const {
    postData: listAllReleasesWithDetails,
    response: listAllReleasesWithDetailsResp,
  } = useListAllReleasesWithDetails(selectedAppGroup);

  const updateReleaseStatusHook = useUpdateReleaseStatus(selectedAppGroup);
  const {
    confirmationDialog: releaseStatusDialog,
    showConfirmationDialog: showReleaseStatusDialog,
  } = useConfirmationDialog(
    `Update Release Status for ${selectedAppGroup}`,
    () => updateReleaseStatusHook,
    displayNotification,
    listAllReleasesWithDetails
  );

  const {
    postData: createReleaseLabel,
    response: createReleaseLabelResp,
    responseStatus: createReleaseLabelRespStatus,
  } = useCreateReleaseLabel(selectedAppGroup);

  useEffect(() => {
    listAllReleasesWithDetails();
  }, [selectedAppGroup]);

  useEffect(() => {
    if (Array.isArray(listAllReleasesWithDetailsResp?.releases)) {
      setReleases(listAllReleasesWithDetailsResp.releases);
      onStatsChange?.(summaryStats(listAllReleasesWithDetailsResp.releases));
    }
  }, [listAllReleasesWithDetailsResp]);

  useEffect(() => {
    if (createReleaseLabelResp) {
      listAllReleasesWithDetails();
    }
  }, [createReleaseLabelResp]);

  const handlePatchLabel = (release, notReadOnly) => {
    const releaseName = release?.name;
    const releaseNumber = releaseName.split("-")[1];
    const transformedRelease = `rel-0.${releaseNumber}.0`;
    setPatchLabelDialog({
      isOpen: true,
      release: transformedRelease,
      notReadOnly: notReadOnly,
      releaseName: releaseName,
      nextVersion: nextPatchLabel(release),
    });
  };
  const resetDialog = () => {
    setPatchLabelDialog(patchLabelDialogDefault);
  };

  useEffect(() => {
    const message =
      createReleaseLabelResp?.message ||
      createReleaseLabelResp?.detail?.message ||
      createReleaseLabelResp?.detail;
    if (message?.length && typeof createReleaseLabelRespStatus === "number") {
      setMessage(message);
      setSeverity(createReleaseLabelRespStatus < 300 ? "success" : "error");
      setNotify(true);
    }
    if (
      typeof createReleaseLabelRespStatus === "number" &&
      createReleaseLabelRespStatus < 300
    ) {
      resetDialog();
    }
  }, [createReleaseLabelResp, createReleaseLabelRespStatus]);

  const archivedReleases = releases.filter(
    (release) => release?.status === "ARCHIVED"
  );
  const activeReleases = releases.filter(
    (release) => release?.status !== "ARCHIVED"
  );
  const selectedRelease =
    activeReleases.find((release) => release?.name === selectedReleaseName) ||
    null;

  return (
    <>
      {notificationComponent}
      {releaseStatusDialog}
      <Notification
        notify={notify}
        severity={severity}
        setNotify={setNotify}
        message={message}
      />
      <PatchLabelDialog
        selectedAppGroup={selectedAppGroup}
        patchLabelDialog={patchLabelDialog}
        createReleaseLabel={createReleaseLabel}
        resetDialog={resetDialog}
      />
      <Box sx={summaryTable.cardList}>
        {activeReleases.map((release) => (
          <ReleaseRow
            key={release?.name}
            release={release}
            handlePatchLabel={handlePatchLabel}
            app={selectedAppGroup}
            showReleaseStatusDialog={showReleaseStatusDialog}
            selected={
              release?.name === selectedReleaseName ||
              release?.name === patchLabelDialog.releaseName
            }
            onSelect={() => setSelectedReleaseName(release?.name)}
          />
        ))}
        {archivedReleases.length ? (
          <ArchivedReleases
            releases={archivedReleases}
            app={selectedAppGroup}
            showReleaseStatusDialog={showReleaseStatusDialog}
          />
        ) : null}
      </Box>
      <ReleaseLabelsDrawer
        release={selectedRelease}
        app={selectedAppGroup}
        showReleaseStatusDialog={showReleaseStatusDialog}
        onClose={() => setSelectedReleaseName(null)}
      />
    </>
  );
};

export default ReleaseSummaryView;
